/**
 * Soft-warning banner for the monthly **points** budget.
 *
 * Renders above the workbench outlet when the org's metered usage crosses
 * a backend-declared warning threshold (50% / 80% / 95%). Dispatches purely
 * on ``warningLevel`` from ``GET /auth/billing/entitlement`` — the threshold
 * matrix is owned server-side (see ``api.org_billing_entitlement
 * .warning_level_for_pct``) so frontend copy and backend gates stay aligned.
 *
 * Distinct from ``OrgCreditRing`` (sidebar nav, always-on, compact). The
 * banner is loud, dismissible, and only renders at info+ levels.
 *
 * Reads ``pointsUsed`` / ``pointsBudget`` (post-M4). Falls back to the
 * legacy ``usedTokens`` / ``includedTokenBudget`` aliases — both fields
 * carry the same number after the migration so dispatch is identical.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Info, X } from 'lucide-react';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import {
  fetchOrgBillingEntitlement,
  fetchOrgUsageHistory,
  isBillingUiEnabled,
} from '@/services/stripeBilling';
import { useTabFocusRefetch } from '@stepflow/ui-shared/hooks/useTabFocusRefetch.js';
import ManageSubscriptionButton from './ManageSubscriptionButton.jsx';
import UpgradeCard from './UpgradeCard.jsx';
import { nextFiniteTierAfter } from '@stepflow/ui-shared/services/generatedPricing';
import {
  normalizeStepflowRole,
  canAccessWorkspaceAdmin,
} from '@/utils/stepflowRoles';

/**
 * Two-layer RBAC gate. Same predicate as ``OrgCreditRing`` — only the
 * stepflow OWNER (account-level, the user who created the org) and
 * workspace OWNER / ADMIN see plan-budget signals. Regular workspace
 * members, BUILDER-only, USER, etc. don't get the banner OR the
 * underlying entitlement fetch.
 */
function canViewOrgBilling(user, workspaceRole) {
  const stepflow = normalizeStepflowRole(user?.stepflowRole);
  if (stepflow === 'OWNER') return true;
  return canAccessWorkspaceAdmin(workspaceRole);
}

const DISMISS_KEY = 'stepflowBillingBudgetBannerDismiss';

const LEVELS = {
  info: {
    icon: Info,
    barClass: 'border-sky-200/90 bg-gradient-to-r from-sky-50 to-sky-50/50',
    iconWrapClass: 'bg-sky-100 text-sky-700',
    titleClass: 'text-sky-950',
    bodyClass: 'text-sky-900/90',
    cta: null,
    title: (pct) => `You've used ${pct}% of this month's plan points.`,
    body: 'No action needed yet — we just want you to see the trend before it accelerates.',
  },
  high: {
    icon: AlertTriangle,
    barClass: 'border-amber-200/90 bg-gradient-to-r from-amber-50 to-amber-50/50',
    iconWrapClass: 'bg-amber-100 text-amber-800',
    titleClass: 'text-amber-950',
    bodyClass: 'text-amber-900/90',
    cta: { label: 'Top up plan', to: '/pricing' },
    title: (pct) => `Approaching your monthly limit — ${pct}% used.`,
    body: 'Top up to avoid runs being paused when the included amount is reached.',
  },
  critical: {
    icon: AlertTriangle,
    barClass: 'border-rose-200/90 bg-gradient-to-r from-rose-50 to-rose-50/50',
    iconWrapClass: 'bg-rose-100 text-rose-700',
    titleClass: 'text-rose-950',
    bodyClass: 'text-rose-900/90',
    cta: { label: 'View plans', to: '/pricing' },
    title: (pct) => `Nearly out of plan points — ${pct}% used.`,
    body: 'Upgrade or top up now to keep new runs flowing this billing period.',
  },
};

function readDismissedKey() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) || '';
  } catch {
    return '';
  }
}

function bannerKey(planKey, usageMonth, level) {
  return `${planKey || 'unknown'}|${usageMonth || 'now'}|${level}`;
}

export default function BillingBudgetBanner() {
  const { user, workspaceRole } = useAuth();
  // Billing UI master switch (single-account/no-Stripe deploys set it false).
  const allowed = isBillingUiEnabled() && canViewOrgBilling(user, workspaceRole);

  const [data, setData] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [dismissedFor, setDismissedFor] = useState(readDismissedKey);

  const refresh = useCallback(async () => {
    if (!allowed) {
      setData(null);
      setUsageHistory([]);
      return;
    }
    try {
      const d = await fetchOrgBillingEntitlement();
      setData(d);
    } catch {
      setData(null);
    }
    // PR 6g — usage history feeds the dollar-impact preview line on the
    // inline UpgradeCard. Best-effort: any fetch error returns ``[]``
    // and the card falls back to plan-spec copy. Don't gate the banner
    // on this fetch.
    try {
      const h = await fetchOrgUsageHistory({ months: 3 });
      setUsageHistory(Array.isArray(h?.months) ? h.months : []);
    } catch {
      setUsageHistory([]);
    }
  }, [allowed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // PR 6d — refetch when the user returns to the StepFlow tab from the
  // Stripe Portal. Banner dispatches on warningLevel; a plan upgrade
  // that lifts the org out of the high/critical band should clear the
  // banner immediately, not on next route change.
  useTabFocusRefetch(refresh, allowed);

  if (!allowed) return null;
  if (!data?.orgId || !data.hasEntitlement || data.unlimited) return null;
  const level = data.warningLevel;
  if (!level || !LEVELS[level]) return null;

  const key = bannerKey(data.planKey, data.usageMonth, level);
  if (dismissedFor === key) return null;

  const cfg = LEVELS[level];
  const Icon = cfg.icon;
  const pct = typeof data.percentUsed === 'number' ? Math.round(data.percentUsed) : 0;

  const onDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, key);
    } catch {
      /* ignore */
    }
    setDismissedFor(key);
  };

  return (
    <div
      className={`flex shrink-0 items-start gap-3 border-b px-4 py-3 sm:items-center sm:px-5 ${cfg.barClass}`}
      role="region"
      aria-label="Plan points warning"
    >
      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${cfg.iconWrapClass}`}>
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${cfg.titleClass}`}>{cfg.title(pct)}</p>
        <p className={`mt-0.5 text-xs ${cfg.bodyClass}`}>{cfg.body}</p>
        {cfg.cta ? (
          <>
            {/* PR 6e — when there's a next finite tier above the current
                plan, replace the generic "Top up plan" link with an
                inline upgrade card. The card pushes a one-click
                upgrade-to-next-tier path; Stripe handles proration so
                the user sees credit on the next invoice. Falls back to
                the legacy "View plans" link only when the current plan
                has no next tier (already on Ultra). */}
            {nextFiniteTierAfter(data.planKey) ? (
              <div className="mt-2 max-w-xl">
                <UpgradeCard
                  currentPlanKey={data.planKey}
                  tone={level === 'critical' ? 'blocked' : 'warning'}
                  allowed={allowed}
                  usageHistory={usageHistory}
                />
              </div>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Link
                  to={cfg.cta.to}
                  className={
                    level === 'critical'
                      ? 'inline-flex items-center rounded-lg bg-rose-700 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-rose-800'
                      : 'inline-flex items-center rounded-lg bg-amber-800 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-amber-900'
                  }
                >
                  {cfg.cta.label}
                </Link>
                <ManageSubscriptionButton allowed={allowed} returnPath="/billing" />
              </div>
            )}
            {/* Manage button is always offered alongside upgrade so an
                admin can cancel / change billing email without going
                through the catalog page. Hidden automatically when no
                Stripe customer exists. */}
            {nextFiniteTierAfter(data.planKey) ? (
              <div className="mt-2">
                <ManageSubscriptionButton allowed={allowed} returnPath="/billing" />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss plan points warning"
        className="ml-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition hover:bg-white/60 hover:text-slate-800"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
